import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable, DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { salesAPI } from '../services/api';
import { SalesLanguage, SalesMedia, SalesProductLine, SalesVariant } from '../types';
import { showCustomAlert } from './CustomAlert';
import SalesMediaLibrary, { MediaPicker, MediaUploadButton, SalesMediaImage } from './SalesMediaLibrary';

type VariantDraft = SalesVariant & { key: string };
type LineDraft = Omit<SalesProductLine, '_id' | 'variants' | 'lambda'> & { lambda: number | null; variants: VariantDraft[] };

const LANGUAGES: Array<{ value: SalesLanguage; label: string }> = [
  { value: 'en', label: 'Engleski' },
  { value: 'sr', label: 'Srpski' },
  { value: 'hu', label: 'Mađarski' },
  { value: 'sk', label: 'Slovački' },
  { value: 'pl', label: 'Poljski' },
  { value: 'de', label: 'Nemački' },
  { value: 'it', label: 'Italijanski' },
  { value: 'tr', label: 'Turski' },
  { value: 'bg', label: 'Bugarski' },
  { value: 'ro', label: 'Rumunski' }
];

const emptyDraft = (): LineDraft => ({ name: '', title: '', lambda: null, icons: [], variants: [], terms: {} });

let nextKey = 1;
const newKey = () => `v${nextKey++}`;
const emptyVariant = (previous?: VariantDraft): VariantDraft => ({
  key: newKey(),
  code: '',
  thickness: '',
  width: previous?.width ?? 1200,
  length: 0,
  piecesPerRoll: previous?.piecesPerRoll ?? 1,
  rollsPerPallet: previous?.rollsPerPallet ?? 24,
  prices: {}
});

// Oznaka "100/50" je polovljena rolna, pa je površina duplo veća
const m2PerRoll = (v: SalesVariant) => (v.width / 1000) * (v.length / 1000) * (v.thickness.includes('/') ? 2 : 1);
// Skriveni redovi ne idu u izvoz, pa im cena nije obavezna
const hasAllPrices = (line: { variants: SalesVariant[] }, lang: SalesLanguage) => {
  const visible = line.variants.filter((v) => !v.hiddenInExport);
  return visible.length > 0 && visible.every((v) => Number(v.prices?.[lang]) > 0);
};
// Podne, da datum ostane isti dan i kada server radi u drugoj vremenskoj zoni
const toNoonIso = (date: Date | null) => (date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12).toISOString() : undefined);
const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;
const cellInput: React.CSSProperties = { width: '100%' };

const SalesPriceLists: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [lines, setLines] = useState<SalesProductLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportLang, setExportLang] = useState<SalesLanguage>('de');
  const [exportingId, setExportingId] = useState('');
  const [selectedLines, setSelectedLines] = useState<SalesProductLine[]>([]);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<SalesProductLine | null>(null);
  const [draft, setDraft] = useState<LineDraft>(emptyDraft);
  const [priceLang, setPriceLang] = useState<SalesLanguage>('de');
  const [copyFrom, setCopyFrom] = useState<SalesLanguage | null>(null);
  const [media, setMedia] = useState<SalesMedia[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await salesAPI.getLines();
      setLines(data);
      // Izbor se čuva posle osvežavanja, bez obrisanih proizvoda
      setSelectedLines((prev) => data.filter((l) => prev.some((p) => p._id === l._id)));
    } catch (e: any) {
      setError(errorMessage(e, 'Greška pri učitavanju cenovnika'));
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async () => {
    try {
      setMedia(await salesAPI.getMedia());
    } catch (e: any) {
      setError(errorMessage(e, 'Greška pri učitavanju biblioteke'));
    }
  };

  useEffect(() => {
    void load();
    void loadMedia();
  }, []);

  const openDialog = (line: SalesProductLine | null) => {
    setEditing(line);
    setDraft(
      line
        ? { name: line.name, title: line.title, lambda: line.lambda, productImageId: line.productImageId || null, sectionImageId: line.sectionImageId || null, icons: line.icons || [], terms: line.terms || {}, variants: line.variants.map((v) => ({ ...v, prices: v.prices || {}, key: newKey() })) }
        : emptyDraft()
    );
    setCopyFrom(null);
    setFormError('');
    setDialogVisible(true);
  };

  const updateVariant = (key: string, patch: Partial<VariantDraft>) =>
    setDraft((d) => ({ ...d, variants: d.variants.map((v) => (v.key === key ? { ...v, ...patch } : v)) }));

  const setPrice = (key: string, value: number | null) =>
    setDraft((d) => ({
      ...d,
      variants: d.variants.map((v) => {
        if (v.key !== key) return v;
        const prices = { ...v.prices };
        if (value === null) delete prices[priceLang];
        else prices[priceLang] = value;
        return { ...v, prices };
      })
    }));

  const setTerm = (patch: { validFrom?: string; deliveryTerm?: string }) =>
    setDraft((d) => ({ ...d, terms: { ...d.terms, [priceLang]: { ...d.terms[priceLang], ...patch } } }));

  const applyCopy = () => {
    if (!copyFrom) return;
    const source = copyFrom;
    const copy = () =>
      setDraft((d) => ({
        ...d,
        variants: d.variants.map((v) => {
          const prices = { ...v.prices };
          if (v.prices[source] === undefined) delete prices[priceLang];
          else prices[priceLang] = v.prices[source];
          return { ...v, prices };
        })
      }));
    if (draft.variants.some((v) => v.prices[priceLang] !== undefined)) {
      showCustomAlert.confirm('Postojeće cene za ovaj jezik biće zamenjene. Nastaviti?', copy, 'Kopiranje cena');
    } else {
      copy();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError('');
    try {
      const payload = { ...draft, lambda: Number(draft.lambda), variants: draft.variants.map(({ key, ...v }) => v) };
      if (editing) await salesAPI.updateLine(editing._id, payload);
      else await salesAPI.createLine(payload);
      setDialogVisible(false);
      await load();
    } catch (e: any) {
      setFormError(errorMessage(e, 'Greška pri čuvanju'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (line: SalesProductLine) => {
    showCustomAlert.confirm(
      `Obrisati proizvod "${line.name}" sa svim cenama i slikama?`,
      async () => {
        try {
          await salesAPI.deleteLine(line._id);
          await load();
        } catch (e: any) {
          showCustomAlert.error(errorMessage(e, 'Greška pri brisanju'));
        }
      },
      'Potvrda brisanja'
    );
  };

  // Više proizvoda ide u jedan fajl: PDF po listovima, Excel po sheet-ovima
  const handleExport = async (exportLines: SalesProductLine[], format: 'pdf' | 'xlsx', key: string) => {
    setExportingId(`${key}-${format}`);
    try {
      const blob = await salesAPI.downloadExport(exportLines.map((l) => l._id), format, exportLang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportLines.length === 1 ? `cenovnik-${exportLines[0].name.replace(/[^a-zA-Z0-9_-]+/g, '-')}-${exportLang}.${format}` : `cenovnici-${exportLang}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e: any) {
      // Odgovor je tražen kao blob, pa je i poruka o grešci u blob-u
      let detail = 'Fajl nije mogao da se napravi';
      try {
        detail = JSON.parse(await e?.response?.data?.text())?.message || detail;
      } catch {
        // ostaje opšta poruka
      }
      toast.current?.show({ severity: 'warn', summary: 'Izvoz nije moguć', detail, life: 10000 });
    } finally {
      setExportingId('');
    }
  };

  const languagesBody = (line: SalesProductLine) => {
    const count = LANGUAGES.filter((l) => hasAllPrices(line, l.value)).length;
    return <Tag value={`${count} / ${LANGUAGES.length}`} severity={count === LANGUAGES.length ? 'success' : count ? 'warning' : 'danger'} />;
  };

  const term = draft.terms[priceLang] || {};
  const validFrom = term.validFrom ? new Date(term.validFrom) : null;

  return (
    <div style={{ padding: '1.5rem' }}>
      <Toast ref={toast} />
      <Card title="Cenovnici" subTitle="Proizvodi, cene po jeziku i izvoz u PDF i Excel">
        <TabView>
          <TabPanel header="Cenovnici">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}
        <div className="flex flex-wrap align-items-center gap-2" style={{ marginBottom: '1rem' }}>
          <span style={{ fontWeight: 600 }}>Jezik izvoza:</span>
          <Dropdown value={exportLang} options={LANGUAGES} optionLabel="label" optionValue="value" onChange={(e) => setExportLang(e.value)} style={{ minWidth: 170 }} />
          {([
            ['pdf', 'PDF izabranih', 'pi pi-file-pdf'],
            ['xlsx', 'Excel izabranih', 'pi pi-file-excel']
          ] as const).map(([format, label, icon]) => (
            <Button
              key={format}
              label={`${label}${selectedLines.length ? ` (${selectedLines.length})` : ''}`}
              icon={icon}
              severity="secondary"
              disabled={!selectedLines.length}
              loading={exportingId === `selected-${format}`}
              onClick={() => void handleExport(selectedLines, format, 'selected')}
            />
          ))}
          <span style={{ flex: 1 }} />
          <Button icon="pi pi-refresh" text tooltip="Osveži" onClick={load} loading={loading} />
          <Button label="Dodaj proizvod" icon="pi pi-plus" onClick={() => openDialog(null)} />
        </div>

        <DataTable
          value={lines}
          loading={loading}
          dataKey="_id"
          emptyMessage="Nema proizvoda"
          responsiveLayout="stack"
          breakpoint="768px"
          selectionMode="checkbox"
          selection={selectedLines}
          onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<SalesProductLine[]>) => setSelectedLines(e.value || [])}
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column field="name" header="Proizvod" sortable />
          <Column field="title" header="Naslov" />
          <Column
            header="Redova"
            body={(l: SalesProductLine) => {
              const hidden = l.variants.filter((v) => v.hiddenInExport).length;
              return (
                <>
                  {l.variants.length}
                  {hidden > 0 && <small style={{ display: 'block', color: '#64748b', whiteSpace: 'nowrap' }}>skriveno: {hidden}</small>}
                </>
              );
            }}
            style={{ width: 90 }}
          />
          <Column header="Jezici sa svim cenama" body={languagesBody} style={{ width: 170 }} />
          <Column
            header="Piktogrami"
            body={(l: SalesProductLine) => (
              <div className="flex gap-1">
                {(l.icons || []).map((id) => (
                  <SalesMediaImage key={id} id={id} alt="" size={30} />
                ))}
              </div>
            )}
            style={{ width: 170 }}
          />
          <Column
            header="Akcije"
            body={(l: SalesProductLine) => (
              <div className="flex gap-2 justify-content-end">
                {([
                  ['pdf', 'PDF', 'pi pi-file-pdf'],
                  ['xlsx', 'Excel', 'pi pi-file-excel']
                ] as const).map(([format, label, icon]) => (
                  <Button
                    key={format}
                    label={label}
                    icon={icon}
                    size="small"
                    outlined
                    loading={exportingId === `${l._id}-${format}`}
                    disabled={!hasAllPrices(l, exportLang)}
                    tooltip={hasAllPrices(l, exportLang) ? undefined : 'Nisu unete sve cene za izabrani jezik'}
                    tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                    onClick={() => void handleExport([l], format, l._id)}
                  />
                ))}
                <Button icon="pi pi-pencil" text rounded tooltip="Izmeni" onClick={() => openDialog(l)} />
                <Button icon="pi pi-trash" text rounded severity="danger" tooltip="Obriši" onClick={() => handleDelete(l)} />
              </div>
            )}
            style={{ width: 300 }}
          />
        </DataTable>
          </TabPanel>
          <TabPanel header="Biblioteka">
            <SalesMediaLibrary
              media={media}
              onChanged={() => void loadMedia()}
              onError={(detail) => toast.current?.show({ severity: 'warn', summary: 'Biblioteka', detail, life: 6000 })}
            />
          </TabPanel>
        </TabView>
      </Card>

      <Dialog
        header={editing ? `Izmeni: ${editing.name}` : 'Dodaj proizvod'}
        visible={dialogVisible}
        style={{ width: 1100, maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        footer={
          <div className="flex flex-column gap-2">
            {formError && <Message severity="error" text={formError} style={{ justifyContent: 'flex-start' }} />}
            <div className="flex gap-2 justify-content-end">
              <Button label="Otkaži" severity="secondary" outlined onClick={() => setDialogVisible(false)} />
              <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSave} loading={saving} />
            </div>
          </div>
        }
      >
        <TabView>
          <TabPanel header="Proizvod">
            <div className="grid">
              <div className="col-12 md:col-4">
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Naziv u tabeli</label>
                <InputText value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="npr. MultiLux" style={cellInput} />
              </div>
              <div className="col-12 md:col-5">
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Naslov u zaglavlju</label>
                <InputText value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="npr. TWOOL MULTILUX" style={cellInput} />
              </div>
              <div className="col-12 md:col-3">
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>λ (W/mK)</label>
                <InputNumber value={draft.lambda} onValueChange={(e) => setDraft((d) => ({ ...d, lambda: e.value ?? null }))} mode="decimal" minFractionDigits={3} maxFractionDigits={3} min={0} locale="sr-RS" style={cellInput} inputStyle={cellInput} />
              </div>
              {([
                ['product', 'Slika proizvoda', 'productImageId'],
                ['section', 'Slika preseka', 'sectionImageId']
              ] as const).map(([kind, label, field]) => (
                <div key={kind} className="col-12 md:col-6">
                  <div className="flex align-items-center justify-content-between" style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 600 }}>{label}</label>
                    <MediaUploadButton
                      kind={kind}
                      label="Dodaj novu"
                      onError={setFormError}
                      onUploaded={(m) => {
                        void loadMedia();
                        setDraft((d) => ({ ...d, [field]: m._id }));
                      }}
                    />
                  </div>
                  <MediaPicker
                    items={media.filter((m) => m.kind === kind)}
                    selected={draft[field] ? [draft[field] as string] : []}
                    onChange={(ids) => setDraft((d) => ({ ...d, [field]: ids[0] || null }))}
                  />
                </div>
              ))}
              <div className="col-12">
                <div className="flex align-items-center justify-content-between" style={{ marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: 600 }}>Piktogrami: gde se proizvod postavlja (redosled klikova je redosled u PDF-u)</label>
                  <MediaUploadButton
                    kind="pictogram"
                    label="Dodaj novi"
                    onError={setFormError}
                    onUploaded={(m) => {
                      void loadMedia();
                      setDraft((d) => ({ ...d, icons: [...d.icons, m._id] }));
                    }}
                  />
                </div>
                <MediaPicker items={media.filter((m) => m.kind === 'pictogram')} selected={draft.icons} multiple thumbSize={64} onChange={(icons) => setDraft((d) => ({ ...d, icons }))} />
              </div>
            </div>
          </TabPanel>

          <TabPanel header={`Redovi (${draft.variants.length})`}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                <thead>
                  <tr style={{ textAlign: 'left', fontSize: 13 }}>
                    <th style={{ padding: 4 }}>U izvozu</th>
                    <th style={{ padding: 4 }}>Šifra</th>
                    <th style={{ padding: 4 }}>Debljina (mm)</th>
                    <th style={{ padding: 4 }}>Širina (mm)</th>
                    <th style={{ padding: 4 }}>Dužina (mm)</th>
                    <th style={{ padding: 4 }}>Kom / rolna</th>
                    <th style={{ padding: 4 }}>Rolni / paleta</th>
                    <th style={{ padding: 4 }}>m² / rolna</th>
                    <th style={{ padding: 4 }}>m² / paleta</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {draft.variants.map((v) => (
                    <tr key={v.key} style={{ opacity: v.hiddenInExport ? 0.5 : 1 }}>
                      <td style={{ padding: 4, textAlign: 'center' }}>
                        <Checkbox
                          inputId={`export-${v.key}`}
                          checked={!v.hiddenInExport}
                          aria-label={`Prikaži red ${v.code} u izvozu`}
                          onChange={(e) => updateVariant(v.key, { hiddenInExport: !e.checked })}
                        />
                      </td>
                      <td style={{ padding: 4 }}><InputText value={v.code} onChange={(e) => updateVariant(v.key, { code: e.target.value })} style={cellInput} /></td>
                      <td style={{ padding: 4 }}><InputText value={v.thickness} onChange={(e) => updateVariant(v.key, { thickness: e.target.value })} placeholder="100 ili 100/50" style={cellInput} /></td>
                      {(['width', 'length', 'piecesPerRoll', 'rollsPerPallet'] as const).map((field) => (
                        <td key={field} style={{ padding: 4 }}>
                          <InputNumber value={v[field]} onValueChange={(e) => updateVariant(v.key, { [field]: Number(e.value || 0) })} useGrouping={false} min={0} style={cellInput} inputStyle={cellInput} />
                        </td>
                      ))}
                      <td style={{ padding: 4, color: '#475569' }}>{m2PerRoll(v).toFixed(2)}</td>
                      <td style={{ padding: 4, color: '#475569' }}>{(m2PerRoll(v) * v.rollsPerPallet).toFixed(2)}</td>
                      <td style={{ padding: 4 }}>
                        <Button icon="pi pi-trash" text rounded severity="danger" aria-label="Ukloni red" onClick={() => setDraft((d) => ({ ...d, variants: d.variants.filter((x) => x.key !== v.key) }))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              label="Dodaj red"
              icon="pi pi-plus"
              size="small"
              outlined
              style={{ marginTop: '0.75rem' }}
              onClick={() => setDraft((d) => ({ ...d, variants: [...d.variants, emptyVariant(d.variants[d.variants.length - 1])] }))}
            />
            <div style={{ color: '#64748b', fontSize: 13, marginTop: '0.75rem' }}>Debljina sa kosom crtom (npr. 100/50) je polovljena rolna: površina se računa duplo. Red bez oznake „U izvozu“ ostaje sačuvan, ali ne ulazi u PDF ni Excel.</div>
          </TabPanel>

          <TabPanel header="Cene po jeziku">
            <div className="flex flex-wrap gap-1" style={{ marginBottom: '1rem' }}>
              {LANGUAGES.map((l) => (
                <Button
                  key={l.value}
                  type="button"
                  label={l.label}
                  size="small"
                  icon={hasAllPrices(draft, l.value) ? 'pi pi-check' : undefined}
                  outlined={priceLang !== l.value}
                  severity={hasAllPrices(draft, l.value) ? 'success' : 'secondary'}
                  onClick={() => {
                    setPriceLang(l.value);
                    setCopyFrom(null);
                  }}
                />
              ))}
            </div>

            {draft.variants.length === 0 ? (
              <Message severity="info" text="Prvo dodajte redove na kartici Redovi." />
            ) : (
              <>
                <div className="grid">
                  <div className="col-12 md:col-3">
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Važi od</label>
                    <Calendar value={validFrom} onChange={(e) => setTerm({ validFrom: toNoonIso(e.value as Date | null) })} dateFormat="dd.mm.yy" showIcon style={cellInput} />
                  </div>
                  <div className="col-12 md:col-5">
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Uslov isporuke</label>
                    <InputText value={term.deliveryTerm || ''} onChange={(e) => setTerm({ deliveryTerm: e.target.value })} placeholder="Prazno = FCO fabrika Šid, na izabranom jeziku" style={cellInput} />
                  </div>
                  <div className="col-12 md:col-4">
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Kopiraj cene iz jezika</label>
                    <div className="flex gap-2">
                      <Dropdown value={copyFrom} options={LANGUAGES.filter((l) => l.value !== priceLang)} optionLabel="label" optionValue="value" placeholder="Izaberite" onChange={(e) => setCopyFrom(e.value)} style={{ flex: 1 }} />
                      <Button type="button" label="Kopiraj" outlined disabled={!copyFrom} onClick={applyCopy} />
                    </div>
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', fontSize: 13 }}>
                      <th style={{ padding: 4 }}>Šifra</th>
                      <th style={{ padding: 4 }}>Debljina (mm)</th>
                      <th style={{ padding: 4, width: 220 }}>Cena EUR/m²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.variants.map((v) => (
                      <tr key={v.key} style={{ borderTop: '1px solid #e2e8f0', opacity: v.hiddenInExport ? 0.5 : 1 }}>
                        <td style={{ padding: 4 }}>
                          {v.code || <span style={{ color: '#94a3b8' }}>bez šifre</span>}
                          {v.hiddenInExport && <small style={{ marginLeft: 8, color: '#64748b' }}>skriven u izvozu</small>}
                        </td>
                        <td style={{ padding: 4 }}>{v.thickness}</td>
                        <td style={{ padding: 4 }}>
                          <InputNumber
                            key={`${v.key}-${priceLang}`}
                            value={v.prices[priceLang] ?? null}
                            onValueChange={(e) => setPrice(v.key, e.value ?? null)}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            min={0}
                            locale="sr-RS"
                            suffix=" €"
                            style={cellInput}
                            inputStyle={cellInput}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </TabPanel>
        </TabView>
      </Dialog>
    </div>
  );
};

export default SalesPriceLists;
